import { useEffect } from 'react';
import { useEffectEvent } from 'use-effect-event';
import useIsLayoutSize from './utils/useIsLayoutSize';
import { LayoutSize, responsiveLayoutSize } from './utils/useLayoutSize';

const breakPointSize = LayoutSize.mobile;

export const getScrollContainer = () => {
  if (responsiveLayoutSize() === breakPointSize) {
    return document.documentElement;
  }
  const content = document.querySelector('#content');
  return content instanceof HTMLElement ? content : null;
};

// Based on empirical testing, 100ms is too short - a single mouse wheel scroll
// can trigger multiple scroll events that last longer than 100ms.
const INTENTIONAL_SCROLL_TOLERANCE = 500;

interface ListenToPostScrollHandlerArgs {
  isScrolledWithUserInteraction: boolean;
}

const listenToPostScroll = (
  handler: (args: ListenToPostScrollHandlerArgs) => void,
) => {
  const scrollContainer = getScrollContainer();

  if (!scrollContainer) {
    return () => {};
  }

  const target =
    scrollContainer === document.documentElement ? document : scrollContainer;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let isScrolledWithUserInteraction = false;

  const onScroll = () => {
    handler({ isScrolledWithUserInteraction });
  };

  const onWheel = () => {
    isScrolledWithUserInteraction = true;

    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      isScrolledWithUserInteraction = false;
    }, INTENTIONAL_SCROLL_TOLERANCE);
  };

  target.addEventListener('scroll', onScroll, { passive: true });
  target.addEventListener('wheel', onWheel, { passive: true });
  return () => {
    target.removeEventListener('scroll', onScroll);
    target.removeEventListener('wheel', onWheel);
    if (timer) clearTimeout(timer);
  };
};

export const useListenToPostScroll = (
  handler: (args: ListenToPostScrollHandlerArgs) => void,
) => {
  const isMatched = useIsLayoutSize(breakPointSize);

  const onScroll = useEffectEvent(handler);
  useEffect(() => {
    return listenToPostScroll(onScroll);
  }, [isMatched]);
};

export default listenToPostScroll;
